import { memo, useMemo, useState } from 'react'
import { useTicker } from 'store/market/hooks'
import { useOrderbook } from 'store/orderbook/hooks'
import { useCurrentSpotSymbol, useSpotSymbols } from 'store/spot/hooks'
import { toFixed, toGrouping } from 'utils/number'
import {
  BaseName,
  CurrentMarket,
  DailyChange,
  LastPrice,
  LastPriceChange,
  LastPriceText,
  LastPriceText2Usd,
  // Line,
  MarketChange,
  MarketHeaderWrap,
  MarketStart,
  QuoteName,
  StatsItem,
  SymbolName,
} from './styles'
import { MarketSelector } from 'components/MarketHeader/MarketSelector'
import { MarketInfo } from 'components/MarketHeader/MarketInfo'
import { StringFormatter } from 'utils/StringFormatter'
import { FieldFilter } from 'components/MarketHeader/FieldFilter'
import { OrderbookCollapsed } from 'components/OrderbookCollapsed'
import { Stack } from '@mui/material'

export const MarketHeader = memo(() => {
  const orderbook = useOrderbook()
  const {
    name = '',
    baseCurrency = '',
    quoteCurrency = '',
  } = useCurrentSpotSymbol()
  const {
    open = 0,
    close = 0,
    high = 0,
    low = 0,
    volume = 0,
    value = 0,
    percent = 0,
  } = useTicker(name) ?? {}
  const symbols = useSpotSymbols()

  const fieldList = useMemo(() => {
    return [
      { label: '24h Change', value: 'change' },
      { label: '24h High', value: 'high' },
      { label: '24h Low', value: 'low' },
      { label: `24h Volume (${baseCurrency})`, value: 'baseVolume' },
      { label: `24h Volume (${quoteCurrency})`, value: 'quoteVolume' },
    ]
  }, [baseCurrency, quoteCurrency])

  const [showFields, setShowFields] = useState<string[]>([
    'change',
    'high',
    'low',
    'baseVolume',
    'quoteVolume',
  ])

  return (
    <MarketHeaderWrap>
      <MarketStart>
        <MarketSelector symbols={symbols}>
          <CurrentMarket>
            <SymbolName id="zk_market_symbol">
              <BaseName>{baseCurrency}</BaseName>
              <QuoteName>
                {quoteCurrency ? `/${quoteCurrency}` : null}
              </QuoteName>
            </SymbolName>
          </CurrentMarket>
        </MarketSelector>
        <LastPrice>
          <LastPriceText $trend={Number(percent) > 0}>
            {toGrouping(orderbook?.price ?? '0')}
          </LastPriceText>
          {/* <LastPriceText2Usd>
            ${toGrouping(orderbook?.price ?? '0')}
          </LastPriceText2Usd> */}
          {/* <LastPriceChange $trend={Number(percent) > 0}>
            {toFixed(close - open, 2)}{' '}
            {StringFormatter.numberSign(toFixed(percent, 2))}%
          </LastPriceChange> */}
        </LastPrice>
      </MarketStart>
      <MarketInfo key={showFields.toString()}>
        <>
          {showFields.includes('change') && (
            <DailyChange>
              24h Change
              <MarketChange $trend={Number(percent) > 0}>
                {toFixed(close - open, 2)}{' '}
                {StringFormatter.numberSign(toFixed(percent, 2))}%
              </MarketChange>
            </DailyChange>
          )}
          {showFields.includes('high') && (
            <StatsItem>
              24h High
              <b>{toGrouping(high)}</b>
            </StatsItem>
          )}
          {showFields.includes('low') && (
            <StatsItem>
              24h Low
              <b>{toGrouping(low)}</b>
            </StatsItem>
          )}
          {showFields.includes('baseVolume') && (
            <StatsItem>
              24h Volume ({baseCurrency})<b>{toGrouping(volume)}</b>
            </StatsItem>
          )}
          {showFields.includes('quoteVolume') && (
            <StatsItem>
              24h Volume ({quoteCurrency})<b>{toGrouping(value)}</b>
            </StatsItem>
          )}
        </>
      </MarketInfo>
      <FieldFilter
        selected={showFields}
        onChange={setShowFields}
        options={fieldList}
      />
      <OrderbookCollapsed />
    </MarketHeaderWrap>
  )
})
